import { useState, useEffect, useRef } from 'react'
import Auth from './components/Auth'
import CollectionSelector from './components/CollectionSelector'
import RuleConfig from './components/RuleConfig'
import AuditList from './components/AuditList'
import GlobalAuditHistory from './components/GlobalAuditHistory'
import Settings from './components/Settings'
import UpgradeModal from './components/UpgradeModal'
import { API_BASE_URL } from './config'
import { useToast } from './context/ToastContext'
import { useNetworkError } from './components/NetworkErrorBoundary'
import { supabase } from './lib/supabase'
import './index.css'

declare global {
  interface Window {
    webflow: any;
  }
}

function App() {
  const { showToast } = useToast();
  const { triggerNetworkError } = useNetworkError();
  
  const [appToken, setAppToken] = useState<string | null>(() => {
    return localStorage.getItem('app_token');
  });

  useEffect(() => {
    if (appToken) {
      localStorage.setItem('app_token', appToken);
    } else {
      localStorage.removeItem('app_token');
    }
  }, [appToken]);

  const [activeView, setActiveView] = useState<'auditor' | 'settings'>('auditor')
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null)
  const [selectedCollectionName, setSelectedCollectionName] = useState<string | null>(null)
  const [auditResults, setAuditResults] = useState<Record<string, { name: string, results: any[], inspectedAt?: number, meta?: any }>>({})
  const [currentRules, setCurrentRules] = useState<any[]>([])
  const [siteId, setSiteId] = useState<string | null>(null)
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null)
  const [isScheduling, setIsScheduling] = useState(false)
  const [siteShortName, setSiteShortName] = useState<string | null>(null)
  const [isInspecting, setIsInspecting] = useState(false)
  const [envStatus, setEnvStatus] = useState<'checking' | 'ready'>('checking')

  const [userProfile, setUserProfile] = useState<{ id?: string, isPremium: boolean, planType?: string, unlockedSiteId?: string | null, freePlanCollectionId?: string | null, freeItemsUsed?: number, siteFreeItemsUsed?: Record<string, number>, invitedWorkspaces?: { id: string, planType: string }[] } | null>(null);
  const isPremium = userProfile?.isPremium === true;
  const isPremiumForSite = userProfile?.planType === 'UNLIMITED' || (userProfile?.planType === 'PERSONAL' && userProfile?.unlockedSiteId === siteId);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isInspectingRef = useRef(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);



  const fetchUserProfile = async (token: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserProfile(data);
      } else if (res.status === 401) {
        setAppToken(null);
        supabase.auth.signOut();
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (appToken) {
      fetchUserProfile(appToken);
    } else {
      setUserProfile(null);
    }
  }, [appToken]);

  useEffect(() => {
    // Check if we just returned from Webflow OAuth
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const authError = params.get('auth_error');
    
    if (urlToken) {
      setAppToken(urlToken);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (authError) {
      showToast(`Authorization failed: ${authError}`, 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Initialize Webflow Designer Extension if running inside Webflow
    const checkWebflow = async (retries = 10) => {
      if (typeof window !== 'undefined' && typeof window.webflow !== 'undefined') {
        console.log("Webflow API found! Setting extension size...");
        
        try {
          // Set the initial extension size when the app loads
          window.webflow.setExtensionSize("large").catch((e: any) => console.error("Failed to set size", e));
          console.log("Extension UI size set to: large");
        } catch (e) {
          console.error("Failed to set size", e);
        }
        setEnvStatus('ready');
      } else if (window.self === window.top) {
        // Not in an iframe! Definitely standalone.
        setEnvStatus('ready');
      } else if (retries > 0) {
        setTimeout(() => checkWebflow(retries - 1), 200);
      } else {
        console.warn("Webflow API not found after 2 seconds of polling.");
        setEnvStatus('ready');
      }
    };
    checkWebflow();

  }, [appToken]);

  useEffect(() => {
    setAuditResults({});
  }, [appToken, siteId]);

  return (
    <div className="h-screen w-screen bg-[var(--background1)] text-[var(--text1)] flex flex-col md:flex-row overflow-hidden font-sans relative">
      {!appToken ? (
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <Auth onAuthSuccess={(token) => setAppToken(token)} />
        </div>
      ) : (
        <>
          {/* Mobile Header */}
          <div className="md:hidden flex items-center justify-between p-3 bg-[var(--background2)] border-b border-[var(--border1)] flex-shrink-0 z-10">
            <div className="font-semibold text-[var(--text1)] text-[14px] md:text-[15px]">CMS Auditor</div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1.5 rounded hover:bg-[var(--background3)] text-[var(--text2)] transition-colors"
              aria-label="Toggle Menu"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Overlay for mobile sidebar */}
          {isMobileMenuOpen && (
            <div 
              className="fixed inset-0 bg-black/40 z-40 md:hidden backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* Left Sidebar */}
          <div className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 w-[240px] flex-shrink-0 bg-[var(--background2)] border-r border-[var(--border1)] flex flex-col h-full ${isMobileMenuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>

            {/* Mobile close button inside sidebar */}
            <div className="md:hidden flex justify-between items-center p-3 border-b border-[var(--border1)]">
              <span className="font-semibold text-[var(--text1)] text-[13px] md:text-[14px]">Menu</span>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 rounded hover:bg-[var(--background3)] text-[var(--text2)] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 flex flex-col min-h-0">
              {envStatus === 'checking' ? (
                <div className="p-4 text-center text-[12px] md:text-[13px] text-[var(--text3)]">
                  <svg className="animate-spin w-5 h-5 text-[var(--text3)] mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Initializing...
                </div>
              ) : (
                <CollectionSelector
                  appToken={appToken}
                  selected={selectedCollection}
                  activeView={activeView}
                  onSettings={() => {
                    setActiveView('settings');
                    setIsMobileMenuOpen(false);
                  }}
                  onSelect={(id, name) => {
                    setSelectedCollection(id);
                    setSelectedCollectionName(name);
                    setActiveView('auditor');
                    setIsMobileMenuOpen(false);
                  }}
                  onSiteResolved={(id, shortName, wsId) => {
                    setSiteId(id);
                    setSiteShortName(shortName);
                    if (wsId) setActiveWorkspaceId(wsId);
                  }}
                  onAuthError={() => {
                    setAppToken(null);
                    supabase.auth.signOut();
                  }}
                  userProfile={userProfile}
                  onUpgradeRequest={() => setShowUpgradeModal(true)}
                  refreshProfile={() => appToken && fetchUserProfile(appToken)}
                />
              )}
            </div>

            {/* Bottom Actions */}
            <div className="p-3 border-t border-[var(--border1)] flex flex-col gap-2">
              {userProfile?.invitedWorkspaces && userProfile.invitedWorkspaces.filter(w => w.id !== activeWorkspaceId).length > 0 && (
                <div className="bg-[#10b981]/10 border border-[#10b981]/20 rounded p-2 mb-1 text-[11px] md:text-[12px] text-[#059669] text-center leading-relaxed relative group cursor-default">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <span className="font-semibold">Workspace Invite Available</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  Switch to Workspace {userProfile.invitedWorkspaces.filter(w => w.id !== activeWorkspaceId).map(w => w.id.substring(0, 8)).join(', ')} to use it.

                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-56 bg-[#111] text-white text-[11px] p-2.5 rounded shadow-lg z-50 border border-[#333] text-left font-normal pointer-events-none">
                    <p className="font-semibold mb-1 text-[12px]">How to connect:</p>
                    <ol className="list-decimal pl-3 space-y-1 text-gray-300">
                      <li>Click your current Workspace name (top left).</li>
                      <li>Select the invited Workspace.</li>
                      <li>Click "Add" to connect a site.</li>
                    </ol>
                  </div>
                </div>
              )}
              {userProfile?.planType === 'UNLIMITED' ? (
                <div className="text-[11px] md:text-[12px] text-[#10b981] text-center font-medium py-2">
                  Unlimited Plan Active
                </div>
              ) : (
                <>
                  {userProfile?.planType === 'FREE' || !userProfile?.isPremium || (userProfile?.planType === 'PERSONAL' && userProfile?.unlockedSiteId !== siteId) ? (
                    <div className="text-[11px] md:text-[12px] text-[var(--text3)] text-center font-medium">
                      {siteId ? (userProfile?.siteFreeItemsUsed?.[siteId] || 0) : 0}/5 free cms items used
                    </div>
                  ) : (
                    <div className="text-[11px] md:text-[12px] text-[#059669] text-center font-medium">
                      Personal Plan Active
                    </div>
                  )}
                  <button
                    disabled={!siteId}
                  onClick={() => setShowUpgradeModal(true)}
                  className={`flex-1 font-medium py-1.5 px-2 rounded-[4px] shadow-sm transition duration-150 text-[12px] md:text-[13px] flex items-center justify-center gap-1.5 ${siteId
                      ? 'bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)]'
                      : 'bg-[var(--background3)] text-[#666] cursor-not-allowed'
                    }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v8m0-8l-3 3m3-3l3 3" />
                    <circle cx="12" cy="12" r="9" strokeDasharray="4 4" strokeWidth={1.5} />
                  </svg>
                  Upgrade
                </button>
                </>
              )}
            </div>
          </div>

          {/* Right Main Pane */}
          <main className="flex-1 flex flex-col h-full bg-[var(--background1)] overflow-hidden relative z-0">
            <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6">
              {activeView === 'settings' ? (
                <div className="flex-1 overflow-y-auto pr-2 pb-6">
                  <Settings 
                    token={appToken} 
                    siteId={siteId} 
                    siteName={siteShortName} 
                    workspaceId={activeWorkspaceId}
                    planType={userProfile?.planType || 'FREE'}
                    unlockedSiteId={userProfile?.unlockedSiteId}
                    onUpgradeClick={() => setShowUpgradeModal(true)}
                    userProfile={userProfile}
                    onLogout={() => {
                      setAppToken(null);
                      supabase.auth.signOut();
                    }}
                  />
                </div>
              ) : selectedCollection ? (
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="flex flex-col sm:flex-row gap-2 flex-none mb-4 md:mb-6">
                    <button
                      className="flex-1 bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] font-medium py-2 px-3 rounded-[4px] transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed text-[13px] md:text-[14px] flex items-center justify-center gap-2"
                      disabled={currentRules.length === 0 || isInspecting}
                      onClick={async () => {
                        if (isInspectingRef.current) return;
                        isInspectingRef.current = true;
                        setIsInspecting(true);
                        
                        const controller = new AbortController();
                        const timeoutId = setTimeout(() => controller.abort(), 30000);
                        
                        try {
                          const res = await fetch(`${API_BASE_URL}/api/inspect/run`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${appToken}`
                            },
                            body: JSON.stringify({
                              collectionId: selectedCollection,
                              siteId: siteId,
                              rules: currentRules
                            }),
                            signal: controller.signal
                          });
                          
                          clearTimeout(timeoutId);
                          
                          if (res.status === 403) {
                            setAuditResults({});
                            setShowUpgradeModal(true);
                            return;
                          }
                          
                          if (!res.ok) {
                            if (res.status === 401) {
                              setAppToken(null);
                              supabase.auth.signOut();
                              return;
                            }
                            if (res.status >= 500) {
                              triggerNetworkError("Our servers are currently experiencing issues. Please try again later.");
                              return;
                            }
                            const errData = await res.json().catch(() => ({}));
                            if (errData.error === 'WRONG_SITE') {
                              setAuditResults({});
                              showToast('Your Personal plan is locked to a different Webflow site. Please upgrade to the Unlimited plan to inspect multiple sites.', 'error');
                              setShowUpgradeModal(true);
                              return;
                            }
                            showToast('Audit failed: ' + (errData.error || res.statusText), 'error');
                            return;
                          }
                          
                          const data = await res.json();
                          if (appToken) fetchUserProfile(appToken); // Re-fetch to lock other collections
                          if (selectedCollection && selectedCollectionName) {
                            setAuditResults(prev => ({
                              ...prev,
                              [selectedCollection]: { 
                                name: selectedCollectionName, 
                                results: data.results || [],
                                meta: data.meta,
                                inspectedAt: Date.now()
                              }
                            }));
                          }
                        } catch (err: any) {
                          console.error(err);
                          if (err.name === 'AbortError' || err.message === 'Failed to fetch' || err.message === 'Network error') {
                             triggerNetworkError("We couldn't reach our servers. Please check your internet connection and try again.");
                          } else {
                             showToast('Audit failed: ' + (err.message || 'Network error'), 'error');
                          }
                        } finally {
                          isInspectingRef.current = false;
                          setIsInspecting(false);
                        }
                      }}
                    >
                      {isInspecting ? (
                        <>
                          <svg className="animate-spin w-4 h-4 text-[var(--text1)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Auditing...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Run Audit
                        </>
                      )}
                    </button>

                    <div className="relative group flex-none w-full sm:w-auto">
                      <button
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 font-medium py-2 px-4 rounded-[4px] transition duration-150 text-[13px] md:text-[14px] ${
                          !isPremiumForSite 
                            ? 'bg-[var(--background1)] border border-[var(--border1)] text-[var(--textInactive)] cursor-not-allowed' 
                            : 'bg-[var(--background3)] hover:bg-[var(--background3)] border border-[var(--border2)] text-[var(--text2)] hover:text-[var(--text1)] disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                        disabled={!isPremiumForSite || currentRules.length === 0 || isScheduling}
                        onClick={async () => {
                          setIsScheduling(true);
                          try {
                            const res = await fetch(`${API_BASE_URL}/api/config/save`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${appToken}`
                              },
                              body: JSON.stringify({
                                collectionId: selectedCollection,
                                collectionName: selectedCollectionName,
                                rules: currentRules,
                                siteId: siteId
                              })
                            });
                            if (res.ok) {
                              showToast('Schedule saved! Audits will now run automatically in the background.', 'success');
                            } else {
                              showToast('Failed to save schedule.', 'error');
                            }
                          } catch (err) {
                            console.error(err);
                            showToast('Error saving schedule', 'error');
                          } finally {
                            setIsScheduling(false);
                          }
                        }}
                      >
                        {isScheduling ? (
                          <>
                            <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Scheduling...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Save & Schedule
                          </>
                        )}
                      </button>
                      
                      {!isPremiumForSite && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 bg-[#111] text-[var(--text1)] text-[11px] md:text-[12px] p-2 rounded shadow-lg z-10 border border-[var(--border1)] font-normal leading-relaxed text-center pointer-events-none">
                          You get this with a premium plan!
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 flex-1 lg:overflow-hidden overflow-y-auto">
                    <div className="w-full lg:flex-1 lg:overflow-y-auto lg:pr-2 lg:pb-6 pb-2">
                      <RuleConfig
                        token={appToken}
                        collectionId={selectedCollection}
                        onChange={setCurrentRules}
                      />
                    </div>

                    <div className="w-full lg:flex-1 lg:overflow-y-auto lg:pr-4 lg:pl-4 border-t lg:border-t-0 lg:border-l border-[var(--border1)] pt-6 lg:pt-0 pb-6 flex flex-col relative min-h-[300px]">
                      {isInspecting && (
                        <div className="absolute top-0 left-0 right-0 z-10 bg-[var(--background1)]/80 backdrop-blur-sm flex flex-col items-center justify-center h-full min-h-[200px]">
                          <svg className="animate-spin w-8 h-8 text-[var(--blueText)] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <p className="text-[13px] md:text-[14px] text-[var(--text1)] font-medium drop-shadow-md">Running inspection on your Webflow items...</p>
                        </div>
                      )}
                      
                      {Object.keys(auditResults || {}).length > 0 ? (
                        <AuditList
                          collectionsData={auditResults}
                          token={appToken}
                          siteId={siteId || undefined}
                          onFixed={(collectionId, itemId, fieldSlug, ruleType) => {
                            // Remove the specific fixed rule from UI instantly
                            setAuditResults(prev => {
                              const colData = prev[collectionId];
                              if (!colData) return prev;
                              return {
                                ...prev,
                                [collectionId]: {
                                  ...colData,
                                  results: colData.results.filter((r: any) => !(r.itemId === itemId && r.fieldSlug === fieldSlug && r.ruleType === ruleType))
                                }
                              };
                            });
                            
                            // We no longer revalidate the item immediately in the background. 
                            // Webflow's API has a cache delay, meaning an immediate GET request often returns the old, unfixed item.
                            // Since the PATCH request already returned 200 OK, we can safely trust it worked and remove it from the UI.
                          }}
                          isPremium={isPremium}
                        />
                      ) : !isInspecting && (
                        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 h-full min-h-[200px]">
                          <svg className="w-10 h-10 text-[var(--textInactive)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-[13px] md:text-[14px] text-[var(--text3)] leading-relaxed">
                            Set inspection rules and run the inspection,<br />or check the Issues tab for site-wide issues.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  <div className="flex flex-col h-full w-full">
                    <GlobalAuditHistory 
                      token={appToken} 
                      siteId={siteId} 
                      onAuthError={() => {
                        setAppToken(null);
                        supabase.auth.signOut();
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </main>
        </>
      )}

      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        token={appToken} 
        siteId={siteId}
        workspaceId={activeWorkspaceId}
        onUpgradeSuccess={() => appToken && fetchUserProfile(appToken)}
        planType={userProfile?.planType || 'FREE'}
        unlockedSiteId={userProfile?.unlockedSiteId}
      />
    </div>
  )
}

export default App
